/**
 * peekStatus — derives the peek-pill label/accent/snippet from runtime state.
 *
 * FR-007: PeekStatus variants and accent colours.
 *
 * Pure helper — no React, no side-effects, fully unit-testable.
 */

import type { VoiceState } from '@/hooks/v2/useVoiceConversation';

// ─── Types ────────────────────────────────────────────────────────────────────

// The four documented variants (FR-007) plus the live voice-call states the
// product already surfaces on the pill (preserved for SC-007 no-regression).
export type PeekVariant =
  | 'replied'
  | 'ready'
  | 'thinking'
  | 'responding'
  | 'listening'
  | 'connecting';

export interface PeekStatusResult {
  variant: PeekVariant;
  label: string;
  accent: string;
  snippet?: string;
}

export interface PeekStatusInput {
  isThinking: boolean;
  /** Text turn streaming a reply — the "responding" phase after `isThinking`. */
  isResponding?: boolean;
  voiceState: VoiceState;
  hasAnakaReply: boolean;
  lastReplyText?: string;
}

// ─── Accent tokens (FR-007) ───────────────────────────────────────────────────

const ACCENT: Record<PeekVariant, string> = {
  replied: '#7C3AED',
  ready: '#22C55E',
  thinking: '#60A5FA',
  responding: '#F59E0B',
  listening: '#22C55E',
  connecting: 'var(--muted-foreground)',
};

const LABEL: Record<PeekVariant, string> = {
  replied: 'Anaka replied',
  ready: 'Ready when you are',
  thinking: 'Thinking…',
  responding: 'Responding',
  listening: 'Listening',
  connecting: 'Connecting…',
};

// ─── derivePeekStatus ─────────────────────────────────────────────────────────

/**
 * Derives the peek-pill display state from the current runtime state.
 *
 * Priority order (highest → lowest). Live voice-call states win over the
 * resting "replied/ready" states so an active call always shows its status:
 *   1. voiceState 'listening'               → listening
 *   2. voiceState 'connecting'              → connecting
 *   3. voiceState 'speaking' | isResponding → responding
 *   4. voiceState 'thinking' | isThinking   → thinking
 *   5. hasAnakaReply (idle)                 → replied
 *   6. everything else                      → ready
 */
export function derivePeekStatus(input: PeekStatusInput): PeekStatusResult {
  const { isThinking, isResponding, voiceState, hasAnakaReply, lastReplyText } = input;

  let variant: PeekVariant;

  if (voiceState === 'listening') {
    variant = 'listening';
  } else if (
    voiceState === 'authorizing' ||
    voiceState === 'connecting' ||
    voiceState === 'preparing' ||
    voiceState === 'reconnecting'
  ) {
    // All connect/recover phases ('authorizing' → 'connecting' → 'preparing',
    // plus mid-session 'reconnecting') share the same compact loading pill.
    variant = 'connecting';
  } else if (voiceState === 'speaking' || isResponding) {
    // Voice agent speaking, or a text turn streaming its reply.
    variant = 'responding';
  } else if (voiceState === 'thinking' || isThinking) {
    variant = 'thinking';
  } else if (hasAnakaReply) {
    variant = 'replied';
  } else {
    variant = 'ready';
  }

  return {
    variant,
    label: LABEL[variant],
    accent: ACCENT[variant],
    snippet: variant === 'replied' && lastReplyText ? lastReplyText : undefined,
  };
}
